import {
  BarChartOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DotChartOutlined,
  LayoutOutlined,
  LineChartOutlined,
  PieChartOutlined,
  MonitorOutlined,
  BarcodeOutlined,
  TableOutlined,
  CarryOutOutlined,
} from '@ant-design/icons';
import React from 'react';
import { Navigate, generatePath } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import {
  getEnabledModuleList,
  usePermissions,
} from 'business/contextProviders/usePermissions';
import { PlotterMode } from 'business/data-analysis/constants';
import Routes from 'config/routes';
import { Module_Enum } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import ListIcon from 'ui/icons/list';
import ShiftReportIcon from 'ui/icons/shift-report';
import { MenuDrawer } from 'ui/menu/menu-drawer';
import MenuEntry from 'ui/menu/menu-entry';
import MenuSection from 'ui/menu/menu-section';

const SHIFT_REPORT_PERMISSIONS = [Module_Enum.ShiftReport];
const DATA_ANALYSIS_PERMISSIONS = [Module_Enum.DataAnalysis];
const PROD_PERF_PERMISSIONS = [Module_Enum.ProdPerf];
const STEERING_PERMISSIONS = [Module_Enum.Steering];
const QUALITY_SCAN_PERMISSIONS = [Module_Enum.QualityScan];
const QUALITY_CONTROL_PERMISSIONS = [Module_Enum.QualityControl];

const REPORTING_SECTION_PERMISSIONS = [...SHIFT_REPORT_PERMISSIONS];
const DATA_ANALYSIS_SECTION_PERMISSIONS = [...DATA_ANALYSIS_PERMISSIONS];
const PROD_PERF_SECTION_PERMISSIONS = [...PROD_PERF_PERMISSIONS];
const STEERING_SECTION_PERMISSIONS = [...STEERING_PERMISSIONS];
const QUALITY_SECTION_PERMISSIONS = [
  ...QUALITY_SCAN_PERMISSIONS,
  ...QUALITY_CONTROL_PERMISSIONS,
];

interface SectionProps {
  onClick: () => void;
  withDescription: boolean;
}

const DataAnalysisSection = ({ onClick, withDescription }: SectionProps) => {
  const { t } = useTranslation();

  return (
    <MenuSection
      title={t('user.menu.sections.dataAnalysis.title')}
      permissions={DATA_ANALYSIS_SECTION_PERMISSIONS}
    >
      <MenuEntry
        route={generatePath(Routes.DataAnalysisGraph, {
          mode: PlotterMode.TIME_PERIOD,
        })}
        icon={<LineChartOutlined />}
        title={t('user.menu.sections.dataAnalysis.timePeriod.title')}
        description={t(
          'user.menu.sections.dataAnalysis.timePeriod.description',
        )}
        permissions={DATA_ANALYSIS_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
      <MenuEntry
        route={generatePath(Routes.DataAnalysisGraph, {
          mode: PlotterMode.RING,
        })}
        icon={<BarChartOutlined />}
        title={t('user.menu.sections.dataAnalysis.ring.title')}
        description={t('user.menu.sections.dataAnalysis.ring.description')}
        permissions={DATA_ANALYSIS_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
    </MenuSection>
  );
};

const ReportingSection = ({ onClick, withDescription }: SectionProps) => {
  const { t } = useTranslation();
  const { currentConstructionSite } = useAppContext();
  const { modules } = usePermissions();

  if (!currentConstructionSite) {
    return <Navigate to={Routes.NO_CONSTRUCTION_SITE} />; // should be handled by router cf: noConstructionSite
  }

  if (modules.SHIFT_REPORT?.isConstructionSiteUser) {
    return (
      <MenuSection
        title={t('user.menu.sections.reporting.title')}
        permissions={REPORTING_SECTION_PERMISSIONS}
      >
        <MenuEntry
          route={Routes.ReportUserHome}
          icon={<ShiftReportIcon />}
          title={t('user.menu.sections.reporting.visualizeShiftReport.title')}
          description={t(
            'user.menu.sections.reporting.visualizeShiftReport.description',
          )}
          permissions={SHIFT_REPORT_PERMISSIONS}
          onClick={onClick}
          withDescription={withDescription}
        />
      </MenuSection>
    );
  }

  return (
    <MenuSection
      title={t('user.menu.sections.reporting.title')}
      permissions={REPORTING_SECTION_PERMISSIONS}
    >
      <MenuEntry
        route={Routes.ReportDashboardVisualize}
        icon={<ShiftReportIcon />}
        title={t('user.menu.sections.reporting.visualizeShiftReport.title')}
        description={t(
          'user.menu.sections.reporting.visualizeShiftReport.description',
        )}
        permissions={SHIFT_REPORT_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
      <MenuEntry
        route={Routes.ReportDashboardAnalyze}
        icon={<PieChartOutlined />}
        title={t('user.menu.sections.reporting.analyzeShiftReport.title')}
        description={t(
          'user.menu.sections.reporting.analyzeShiftReport.description',
        )}
        permissions={SHIFT_REPORT_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
    </MenuSection>
  );
};

const ProdPerfSection = ({ onClick, withDescription }: SectionProps) => {
  const { t } = useTranslation();

  return (
    <MenuSection
      title={t('user.menu.sections.prodPerf.title')}
      permissions={[
        ...PROD_PERF_SECTION_PERMISSIONS,
        ...STEERING_SECTION_PERMISSIONS,
      ]}
    >
      <MenuEntry
        route={Routes.ProdPerfRingList}
        icon={<ListIcon />}
        title={t('user.menu.sections.prodPerf.ringList.title')}
        description={t('user.menu.sections.prodPerf.ringList.description')}
        permissions={PROD_PERF_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
      <MenuEntry
        route={Routes.ProdPerfRingPerDay}
        icon={<BarChartOutlined />}
        title={t('user.menu.sections.prodPerf.ringPerDay.title')}
        description={t('user.menu.sections.prodPerf.ringPerDay.description')}
        permissions={PROD_PERF_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
      <MenuEntry
        route={Routes.ProdPerfExcavationBuildTime}
        icon={<ClockCircleOutlined />}
        title={t('user.menu.sections.prodPerf.excavationBuildTime.title')}
        description={t(
          'user.menu.sections.prodPerf.excavationBuildTime.description',
        )}
        permissions={PROD_PERF_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
      <MenuEntry
        route={Routes.ProdPerfPlanning}
        icon={<CalendarOutlined />}
        title={t('user.menu.sections.prodPerf.planning.title')}
        description={t('user.menu.sections.prodPerf.planning.description')}
        permissions={PROD_PERF_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
      <MenuEntry
        route={Routes.SteeringTable}
        icon={<DotChartOutlined />}
        title={t('user.menu.sections.steering.table.title')}
        description={t('user.menu.sections.steering.table.description')}
        permissions={STEERING_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
    </MenuSection>
  );
};

const DashboardEntry = ({ onClick, withDescription }: SectionProps) => {
  const { t } = useTranslation();

  return (
    <MenuSection permissions={[Module_Enum.Dashboard]}>
      <MenuEntry
        route={Routes.App}
        title={t('user.menu.dashboard.title')}
        icon={<LayoutOutlined />}
        description={t('user.menu.dashboard.description')}
        permissions={[Module_Enum.Dashboard]}
        onClick={onClick}
        withDescription={withDescription}
      />
    </MenuSection>
  );
};

const QualityControlSection = ({ onClick, withDescription }: SectionProps) => {
  const { t } = useTranslation();

  const {
    isDataManager,
    modules: {
      QUALITY_CONTROL: { isConstructionSiteManager, isConstructionSiteReader },
    },
  } = usePermissions();

  const { currentConstructionSite } = useAppContext();
  if (!currentConstructionSite) {
    return <Navigate to={Routes.NO_CONSTRUCTION_SITE} />; // should be handled by router cf: noConstructionSite
  }

  return (
    <MenuSection
      title={t('user.menu.sections.qualityControl.title')}
      permissions={QUALITY_SECTION_PERMISSIONS}
    >
      <MenuEntry
        route={Routes.QualitySegmentHistory}
        icon={<BarcodeOutlined />}
        title={t('user.menu.sections.qualityControl.segmentHistory.title')}
        description={t(
          'user.menu.sections.qualityControl.segmentHistory.description',
        )}
        permissions={QUALITY_SCAN_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
      {isDataManager || isConstructionSiteManager ? (
        <MenuEntry
          route={Routes.QualityRecordSegmentDefect}
          icon={<MonitorOutlined />}
          title={t(
            'user.menu.sections.qualityControl.recordSegmentDefect.title',
          )}
          description={t(
            'user.menu.sections.qualityControl.recordSegmentDefect.description',
          )}
          permissions={QUALITY_CONTROL_PERMISSIONS}
          onClick={onClick}
          withDescription={withDescription}
        />
      ) : null}
      {isDataManager ||
      isConstructionSiteManager ||
      isConstructionSiteReader ? (
        <MenuEntry
          route={Routes.QualityDefectReportHistory}
          icon={<TableOutlined />}
          title={t(
            'user.menu.sections.qualityControl.defectReportHistory.title',
          )}
          description={t(
            'user.menu.sections.qualityControl.defectReportHistory.description',
          )}
          permissions={QUALITY_CONTROL_PERMISSIONS}
          onClick={onClick}
          withDescription={withDescription}
        />
      ) : null}
      <MenuEntry
        route={Routes.QualityRingComplianceReport}
        icon={<CarryOutOutlined />}
        title={t(
          'user.menu.sections.qualityControl.ringComplianceReport.title',
        )}
        description={t(
          'user.menu.sections.qualityControl.ringComplianceReport.description',
        )}
        permissions={QUALITY_CONTROL_PERMISSIONS}
        onClick={onClick}
        withDescription={withDescription}
      />
    </MenuSection>
  );
};

export const Menu = ({
  menuState: { open, setOpen },
}: {
  menuState: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
}) => {
  const {
    isDataManager,
    modules: {
      SHIFT_REPORT: { isConstructionSiteUser: isShiftReportUser },
      QUALITY_CONTROL: { isConstructionSiteUser: isQualityControlUser },
    },
  } = usePermissions();
  const { currentConstructionSite } = useAppContext();
  const { isMobile, isTablet } = useMediaType();
  invariant(currentConstructionSite, 'No constructionSite id');

  const enabledModules = getEnabledModuleList(
    isDataManager,
    currentConstructionSite,
  );

  const withDescription = !isMobile && !isTablet;

  const hide = () => {
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  if (
    enabledModules.length === 1 &&
    enabledModules[0] === Module_Enum.ShiftReport
  ) {
    if (isShiftReportUser) {
      return null;
    }

    return (
      <MenuDrawer open={open} onOpenChange={handleOpenChange}>
        <ReportingSection onClick={hide} withDescription={withDescription} />
      </MenuDrawer>
    );
  }

  if (
    enabledModules.length === 1 &&
    enabledModules[0] === Module_Enum.QualityControl &&
    isQualityControlUser
  ) {
    return null;
  }

  const menuContent = (
    <>
      <DashboardEntry onClick={hide} withDescription={withDescription} />
      <DataAnalysisSection onClick={hide} withDescription={withDescription} />

      <ProdPerfSection onClick={hide} withDescription={withDescription} />

      <ReportingSection onClick={hide} withDescription={withDescription} />

      <QualityControlSection onClick={hide} withDescription={withDescription} />
    </>
  );

  return (
    <MenuDrawer open={open} onOpenChange={handleOpenChange}>
      {menuContent}
    </MenuDrawer>
  );
};
